<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { Waypoint, type WaypointState } from 'vue-waypoint';

// Import from KaLoader's own entrypoint, not the `@/kasten-components/components` barrel: the barrel
// pulls in the entire library (incl. chart components with CJS deps), which breaks consumers loaded
// in the Histoire sandbox ("require is not defined").
import { KaLoader } from '@/kasten-components/components/KaLoader';

const root = useTemplateRef('root');

export type KaInfiniteLoaderProps = {
  /** Provide this only if the parent element is NOT the scroll container. */
  scrollContainer?: HTMLElement | null;
  /** Is the fetch request pending? */
  loadingMore: boolean;
  /** The fetched data. Required so this component can determine when to fetch more data. */
  data: any[];
  /** The total number of results. Required so this component can determine when to fetch more data. */
  totalCount: number;
  noMoreResultsMessage?: string;
  /** Whether to hide the bottom message when there are no more results. */
  hideBottomMessage?: boolean;
};

const props = withDefaults(defineProps<KaInfiniteLoaderProps>(), {
  noMoreResultsMessage: 'No more results',
});

const emit = defineEmits<{
  loadMore: [];
}>();

const hasMore = computed(() => props.data.length < props.totalCount);

const hasData = computed(() => props.data.length > 0);

const enabled = computed(
  () =>
    // load more only after the the first page has been loaded
    hasData.value &&
    // load only when no other fetch is pending
    !props.loadingMore &&
    // load only when more data available
    hasMore.value,
);
</script>

<template>
  <!-- hide the div if no more results -->
  <div
    ref="root"
    :class="[
      'flex items-center justify-center',
      {
        'py-1': !props.loadingMore && hideBottomMessage, // maintain some space so that the waypoint can be reached, otherwise if hideBottomMessage is true, the waypoint can never be reached
        'py-4': props.loadingMore || !hideBottomMessage,
      },
    ]"
  >
    <KaLoader v-if="props.loadingMore" />

    <Waypoint
      data-testid="infinite-loader-waypoint"
      :active="enabled"
      :options="{
        root: props.scrollContainer ?? root?.parentElement,
      }"
      @change="
        (state: WaypointState) => {
          if (state.going === 'IN' && enabled) {
            emit('loadMore');
          }
        }
      "
    />

    <span v-if="!hasMore && !props.loadingMore && !hideBottomMessage" class="text-sm">
      {{ props.noMoreResultsMessage }}
    </span>
  </div>
</template>
